import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Grid } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { FieldLabel } from '../styles';
import { getEstateUnits } from '../services/estate-unit';
import { EstateUnit, EstateUnitStatusEnum } from '../types/EstateUnit';
import { iRootState } from '../store';
import { UserRolesEnum } from '../enums/user-roles.enum';
import {
  ESTATE_UNIT_FILTER_ALL,
  ESTATE_UNIT_FILTER_NONE
} from '../utils/estateUnitFilter.util';

export type EstateUnitFilterProps = {
  estateId?: string;
  value: string;
  onChange: (value: string) => void;
  userRole?: string;
  userEstateUnitId?: string | null;
  fluid?: boolean;
  columnWidth?: React.ComponentProps<typeof Grid.Column>['width'];
};

const EstateUnitFilterComponent: React.FC<EstateUnitFilterProps> = ({
  estateId,
  value,
  onChange,
  userRole,
  userEstateUnitId,
  fluid = true,
  columnWidth
}) => {
  const [units, setUnits] = useState<EstateUnit[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const isEstateUserWithUnit =
    userRole === UserRolesEnum.estateUser && Boolean(userEstateUnitId);

  useEffect(() => {
    if (!estateId || isEstateUserWithUnit) {
      setUnits([]);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    getEstateUnits({ estateId, status: EstateUnitStatusEnum.active })
      .then(response => {
        if (isMounted) {
          setUnits(response || []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setUnits([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [estateId, isEstateUserWithUnit]);

  const options = useMemo(() => {
    const baseOptions = [
      { key: 'all', text: 'Todas', value: ESTATE_UNIT_FILTER_ALL },
      ...units.map(unit => ({
        key: unit.id,
        text: unit.name,
        value: unit.id
      })),
      { key: 'none', text: 'Sem unidade', value: ESTATE_UNIT_FILTER_NONE }
    ];

    return baseOptions;
  }, [units]);

  if (!estateId || isEstateUserWithUnit || units.length === 0) {
    return null;
  }

  const content = (
    <>
      <FieldLabel>Unidade</FieldLabel>
      <Dropdown
        fluid={fluid}
        search
        clearable
        selection
        placeholder="Unidade"
        loading={isLoading}
        options={options}
        value={value}
        onChange={(_, { value: selectedValue }) =>
          onChange(String(selectedValue || ESTATE_UNIT_FILTER_ALL))
        }
      />
    </>
  );

  if (columnWidth != null) {
    return <Grid.Column width={columnWidth}>{content}</Grid.Column>;
  }

  return content;
};

const mapState = (state: iRootState) => ({
  userRole: state.user.role,
  userEstateUnitId: state.user.estateUnitId
});

export default connect(mapState)(EstateUnitFilterComponent);
